import React, { useMemo, useState } from 'react';
import { Box, CircularProgress, GlobalStyles, Typography, useTheme } from '@mui/material';
import moment from 'moment';
import { Calendar, Event, EventProps, momentLocalizer, View } from 'react-big-calendar';
import { getCalendarColorPalette } from 'dashboard409a/hooks/useColorPalette';
import { STATUS_COLORS } from 'dashboard409a/pages/Workboard/common/constants';
import { ValuationStatus } from 'dashboard409a/pages/Workboard/common/types';
import { ValuationCalendarEvent, ValuationsCalendarProps } from './ValuationsCalendar.types';
import {
  CALENDAR_DATE_FORMAT,
  CALENDAR_EVENT_BASE_STYLES,
  CALENDAR_MESSAGES,
  CALENDAR_STYLES,
  CALENDAR_VIEWS,
  DEFAULT_CALENDAR_VIEW,
} from '../../common/constants/calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';

import '../ModelsCalendar/styles.scss';

const NO_VALUATIONS_WITH_DATES = 'No valuations with valuation dates found';

const localizer = momentLocalizer(moment);

const VALUATION_STATUS_COLOR_MAP: Record<string, string> = {
  [ValuationStatus.READY_FOR_ONBOARDING]: STATUS_COLORS[ValuationStatus.READY_FOR_ONBOARDING],
  [ValuationStatus.ONBOARDING_SENT]: STATUS_COLORS[ValuationStatus.ONBOARDING_SENT],
  [ValuationStatus.ONBOARDING_FINISHED]: STATUS_COLORS[ValuationStatus.ONBOARDING_FINISHED],
  [ValuationStatus.CLIENT_DOCS_RECEIVED]: STATUS_COLORS[ValuationStatus.CLIENT_DOCS_RECEIVED],
  [ValuationStatus.CLIENT_DOCS_RECEIVED_SKIP_SUPPORT]: STATUS_COLORS[ValuationStatus.CLIENT_DOCS_RECEIVED_SKIP_SUPPORT],
  [ValuationStatus.CLIENT_DOCS_RECEIVED_ASSIGN_INTERN]: STATUS_COLORS[ValuationStatus.CLIENT_DOCS_RECEIVED_ASSIGN_INTERN],
  [ValuationStatus.MODEL_DRAFTED]: STATUS_COLORS[ValuationStatus.MODEL_DRAFTED],
  [ValuationStatus.DRAFT_PREPARED]: STATUS_COLORS[ValuationStatus.DRAFT_PREPARED],
  [ValuationStatus.DRAFT_REVIEWED_AND_SENT]: STATUS_COLORS[ValuationStatus.DRAFT_REVIEWED_AND_SENT],
  [ValuationStatus.CLIENT_APPROVAL_RECEIVED]: STATUS_COLORS[ValuationStatus.CLIENT_APPROVAL_RECEIVED],
  [ValuationStatus.REPORT_IN_PROCESS]: STATUS_COLORS[ValuationStatus.REPORT_IN_PROCESS],
  [ValuationStatus.FINAL_REPORT_UPLOADED]: STATUS_COLORS[ValuationStatus.FINAL_REPORT_UPLOADED],
  [ValuationStatus.FINAL_SENT]: STATUS_COLORS[ValuationStatus.FINAL_SENT],
  [ValuationStatus.WAITING]: STATUS_COLORS[ValuationStatus.WAITING],
  [ValuationStatus.AWAITING_FINAL_PAYMENT]: STATUS_COLORS[ValuationStatus.AWAITING_FINAL_PAYMENT],
  [ValuationStatus.CANCELLED]: STATUS_COLORS[ValuationStatus.CANCELLED],
};

const getValuationStatusColor = (status: string | null): string =>
  (status && VALUATION_STATUS_COLOR_MAP[status]) || '#E3F2FD';

const EventComponent = (props: EventProps) => {
  const theme = useTheme();
  const { event } = props;
  const calendarEvent = event as ValuationCalendarEvent;
  const valuation = calendarEvent.resource;
  const companyName = valuation.company_name || CALENDAR_MESSAGES.UNKNOWN_COMPANY;
  const valuationDate = valuation.valuation_date ? moment(valuation.valuation_date).format('MMM, DD, YY') : '';
  const statusColor = getValuationStatusColor(valuation.status);

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', padding: 0.5, margin: '4px 0' }}>
      <Box display="flex" flexDirection="column" justifyContent="space-between" sx={{ backgroundColor: statusColor, padding: 0.5, borderRadius: 1 }}>
        <Typography variant="caption" fontSize="0.6rem" fontWeight="bold" sx={{ color: theme.palette.getContrastText(statusColor) }}>
          {valuation.status || '-'}
        </Typography>
      </Box>
      <Box display="flex" flexDirection="row" justifyContent="space-between">
        <Typography
          variant="caption"
          fontWeight="bold"
          sx={{
            lineHeight: 1.2,
            color: 'text.primary',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {companyName}
        </Typography>
      </Box>
      {valuationDate && (
        <Box display="flex" sx={{ padding: 0.5, borderRadius: 1 }}>
          <Box display="flex" flexDirection="column" justifyContent="space-between" sx={{ backgroundColor: getCalendarColorPalette().LABELS, padding: 0.5, borderRadius: 1 }}>
            <Typography variant="caption" fontSize="0.7rem" sx={{ color: theme.palette.getContrastText(getCalendarColorPalette().LABELS) }}>
              {valuationDate}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

const ValuationsCalendar = ({
  valuations,
  isLoading,
  onSelectEvent,
  hideToolbar = false,
  controlledDate,
  controlledView,
  onDateChange,
  onViewChange: onViewChangeProp,
}: ValuationsCalendarProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { default: bg, paper } = theme.palette.background;
  const border = theme.palette.divider;
  const textPrimary = theme.palette.text.primary;
  const textSecondary = theme.palette.text.secondary;
  const [currentDate, setCurrentDate] = useState(() => new Date());
  const [currentView, setCurrentView] = useState<View>(DEFAULT_CALENDAR_VIEW);

  const events: ValuationCalendarEvent[] = useMemo(() => valuations
    .filter(v => {
      const valuationDate = v.valuation_date;
      return v.record_id && valuationDate && valuationDate !== '-' && valuationDate.trim();
    })
    .map(v => {
      const valuationDate = v.valuation_date as string;
      const date = moment(valuationDate, CALENDAR_DATE_FORMAT);

      if (!date.isValid()) {
        return null;
      }

      return {
        title: v.company_name || CALENDAR_MESSAGES.UNKNOWN_COMPANY,
        start: date.toDate(),
        end: date.toDate(),
        resource: v,
      } as ValuationCalendarEvent;
    })
    .filter((event): event is ValuationCalendarEvent => event !== null), [valuations]);

  const handleNavigate = (newDate: Date) => {
    if (onDateChange) {
      onDateChange(newDate);
    } else {
      setCurrentDate(newDate);
    }
  };

  const handleViewChange = (newView: View) => {
    if (onViewChangeProp) {
      onViewChangeProp(newView);
    } else {
      setCurrentView(newView);
    }
  };

  const activeDate = controlledDate ?? currentDate;
  const activeView = controlledView ?? currentView;

  const eventStyleGetter = () => ({
    style: {
      ...CALENDAR_EVENT_BASE_STYLES,
      backgroundColor: isDark ? paper : getCalendarColorPalette().BACKGROUND,
      border: `${isDark ? border : getCalendarColorPalette().BORDER} 1px solid`,
      opacity: CALENDAR_STYLES.EVENT_OPACITY,
      color: textPrimary,
    },
  });

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (events.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography variant="body1" color="text.secondary">
          {NO_VALUATIONS_WITH_DATES}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ padding: 2 }}>
      {isDark && (
        <GlobalStyles styles={{
          '.rbc-calendar, .rbc-month-view, .rbc-time-view, .rbc-agenda-view': {
            backgroundColor: bg,
            color: textPrimary,
          },
          '.rbc-month-header, .rbc-header': {
            backgroundColor: `${paper} !important`,
            color: `${textPrimary} !important`,
            borderColor: `${border} !important`,
          },
          '.rbc-day-bg': {
            backgroundColor: bg,
          },
          '.rbc-off-range-bg': {
            backgroundColor: `${paper} !important`,
          },
          '.rbc-today': {
            backgroundColor: `${theme.palette.primary.dark}22 !important`,
          },
          '.rbc-month-row, .rbc-day-slot': {
            borderColor: `${border} !important`,
          },
          '.rbc-header + .rbc-header, .rbc-month-view .rbc-header': {
            borderColor: `${border} !important`,
          },
          '.rbc-month-view': {
            borderColor: `${border} !important`,
          },
          '.rbc-day-bg + .rbc-day-bg': {
            borderColor: `${border} !important`,
          },
          '.rbc-month-row + .rbc-month-row': {
            borderColor: `${border} !important`,
          },
          '.rbc-date-cell': {
            color: textSecondary,
          },
          '.rbc-date-cell.rbc-off-range': {
            color: `${textSecondary}80`,
          },
          '.rbc-toolbar button': {
            color: `${textPrimary} !important`,
            borderColor: `${border} !important`,
          },
          '.rbc-toolbar button.rbc-active, .rbc-toolbar button:hover': {
            backgroundColor: `${theme.palette.action.selected} !important`,
            color: `${textPrimary} !important`,
          },
          '.rbc-event': {
            backgroundColor: paper,
            borderColor: `${border} !important`,
          },
          '.rbc-show-more': {
            color: `${theme.palette.primary.light} !important`,
          },
        }} />
      )}
      <Calendar
        localizer={localizer}
        events={events}
        startAccessor="start"
        endAccessor="end"
        date={activeDate}
        view={activeView}
        onNavigate={handleNavigate}
        onView={handleViewChange}
        toolbar={!hideToolbar}
        showAllEvents
        onSelectEvent={(event: Event) => {
          const calendarEvent = event as ValuationCalendarEvent;
          onSelectEvent?.(calendarEvent.resource);
        }}
        eventPropGetter={eventStyleGetter}
        components={{
          event: EventComponent,
        }}
        views={[...CALENDAR_VIEWS]}
        popup
        showMultiDayTimes={false}
      />
    </Box>
  );
};

export default ValuationsCalendar;
